import { useEffect, useRef, useState } from 'react'

/**
 * Le sens du défilement — [DECISIONS §147].
 *
 * **On masque en descendant, on rend en remontant.** Descendre, c'est vouloir
 * du contenu ; remonter, c'est chercher une commande. Le menu secondaire suit
 * cette intention au lieu d'occuper en permanence une bande de 56 px — sur un
 * écran de 360 × 640, l'en-tête et les onglets mangeaient ensemble le sixième
 * de la hauteur utile.
 *
 * **Un seuil, sinon ça tremble.** Sans lui, le moindre soubresaut du doigt fait
 * clignoter la barre : un défilement réel n'est jamais parfaitement monotone, et
 * réagir au pixel près donne une interface nerveuse. Douze pixels séparent le
 * geste du frémissement.
 *
 * **Toujours visible en haut de page.** Repartir du sommet doit toujours
 * remontrer les onglets, quel que soit le sens du dernier geste — sinon le
 * candidat qui a remonté d'un coup de pouce se retrouve sans menu là où il y en
 * a toujours eu un.
 */
export function useDefilement(seuil = 12): boolean {
  const [visible, setVisible] = useState(true);
  const dernier = useRef(0);

  useEffect(() => {
    dernier.current = window.scrollY;

    function auDefilement() {
      const y = window.scrollY;
      const ecart = y - dernier.current;

      // En haut de page, rien ne se masque : il n'y a pas encore eu de geste
      // à interpréter, et une barre absente y passerait pour un défaut.
      if (y <= 0) {
        dernier.current = 0;
        setVisible(true);

        return;
      }

      if (Math.abs(ecart) < seuil) {
        return;
      }

      dernier.current = y;
      setVisible(ecart < 0);
    }

    // `passive` : le gestionnaire ne peut pas annuler le défilement, et le dire
    // au navigateur lui permet de ne pas l'attendre avant de peindre. Sur le
    // Moto G4 qui sert de cible, c'est la différence entre un défilement fluide
    // et un défilement qui accroche.
    window.addEventListener('scroll', auDefilement, { passive: true });

    return () => { window.removeEventListener('scroll', auDefilement); };
  }, [seuil]);

  return visible;
}
